<template>
  <div class="so-order-item-requests-editor">
    <div class="requests-list">
      <div v-if="localRequests.length === 0" class="empty-state">
        <p>{{ t('Nenhuma solicitação adicionada') }}</p>
      </div>

      <div v-for="(request, index) in localRequests" :key="index" class="request-row">
        <div class="request-content">
          <!-- Description Input -->
          <div class="form-field">
            <label :for="`request-description-${index}`" class="field-label">
              {{ t('Descrição') }} <span class="required-asterisk">*</span>
            </label>
            <input-text
              :id="`request-description-${index}`"
              v-model="request.description"
              :elem-id="`pdv_so_request_description-${index}`"
              :data-testid="`request-description-${index}`"
              :placeholder="t('Descreva a solicitação do cliente')"
              type="text"
              required
              :validation="() => !!request.description && request.description.trim().length > 0"
              @update:modelValue="handleRequestChange(index)"
            />
          </div>

          <!-- Photo Capture -->
          <div class="form-field">
            <label class="field-label">{{ t('Foto') }}</label>
            <CameraCapture
              :index="index"
              :data-testid="`request-photo-${index}`"
              @photo-captured="(file: File) => handlePhotoCaptured(index, file)"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="request-actions">
          <!-- Move Up Button -->
          <button
            type="button"
            :data-testid="`request-move-up-${index}`"
            class="action-btn move-btn"
            :disabled="index === 0"
            :title="t('Mover para cima')"
            @click="moveUp(index)"
          >
            <i class="pi pi-arrow-up"></i>
          </button>

          <!-- Move Down Button -->
          <button
            type="button"
            :data-testid="`request-move-down-${index}`"
            class="action-btn move-btn"
            :disabled="index === localRequests.length - 1"
            :title="t('Mover para baixo')"
            @click="moveDown(index)"
          >
            <i class="pi pi-arrow-down"></i>
          </button>

          <!-- Remove Button -->
          <button
            type="button"
            :data-testid="`remove-request-${index}`"
            class="action-btn remove-btn"
            :title="t('Remover solicitação')"
            @click="removeRequest(index)"
          >
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add Request Button -->
    <div class="add-button-container">
      <button
        type="button"
        :data-testid="'add-request-btn'"
        class="add-btn"
        @click="addRequest"
      >
        <i class="pi pi-plus"></i>
        {{ t('Adicionar Solicitação') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface OrderItemRequest {
  description?: string;
  photo?: string | null;
  photo_file?: File | null;
  display_order?: number;
  [key: string]: unknown;
}

interface Props {
  modelValue?: OrderItemRequest[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => []
});

const emit = defineEmits<{
  'update:modelValue': [requests: OrderItemRequest[]];
}>();

const { t } = useI18n();

// Local copy of requests for editing
const localRequests = ref<OrderItemRequest[]>([]);

// Initialize from modelValue — use shallow spread to preserve File objects
watch(
  () => props.modelValue,
  (newVal: OrderItemRequest[] | undefined) => {
    if (Array.isArray(newVal)) {
      localRequests.value = newVal.map((req: OrderItemRequest) => ({ ...req }));
    }
  },
  { immediate: true }
);

// Emit changes back to parent
const emitChanges = (): void => {
  const requests = localRequests.value.map((req: OrderItemRequest, index: number) => ({
    description: req.description || '',
    photo: req.photo || null,
    photo_file: req.photo_file || null,
    display_order: index
  }));
  emit('update:modelValue', requests);
};

// Add new request
const addRequest = (): void => {
  localRequests.value.push({
    description: '',
    photo: null,
    photo_file: null,
    display_order: localRequests.value.length
  });
  emitChanges();
};

// Remove request at index
const removeRequest = (index: number): void => {
  localRequests.value.splice(index, 1);
  emitChanges();
};

// Move request up
const moveUp = (index: number): void => {
  if (index > 0) {
    [localRequests.value[index - 1], localRequests.value[index]] = [
      localRequests.value[index],
      localRequests.value[index - 1]
    ];
    emitChanges();
  }
};

// Move request down
const moveDown = (index: number): void => {
  if (index < localRequests.value.length - 1) {
    [localRequests.value[index], localRequests.value[index + 1]] = [
      localRequests.value[index + 1],
      localRequests.value[index]
    ];
    emitChanges();
  }
};

// Handle request field change
const handleRequestChange = (_index: number): void => {
  emitChanges();
};

// Handle photo capture
const handlePhotoCaptured = (index: number, file: File): void => {
  const updated = [...localRequests.value];
  updated[index] = { ...updated[index], photo_file: file };
  localRequests.value = updated;
  emitChanges();
};
</script>

<style scoped>
.so-order-item-requests-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 50px;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}

.request-row {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--p-surface-border);
  border-radius: 4px;
  background-color: var(--p-surface-50);
  align-items: flex-start;
}

.request-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--p-text-color);
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.required-asterisk {
  color: var(--p-red-500);
}

.request-actions {
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  justify-content: flex-start;
}

.action-btn {
  padding: 0.5rem;
  border: 1px solid var(--p-surface-border);
  background-color: var(--p-surface-0);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 40px;
  height: 40px;
}

.action-btn:hover:not(:disabled) {
  background-color: var(--p-primary-50);
  border-color: var(--p-primary-300);
  color: var(--p-primary-color);
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn i {
  font-size: 0.9rem;
}

.remove-btn {
  color: var(--p-red-500);
}

.remove-btn:hover:not(:disabled) {
  background-color: var(--p-red-50);
  border-color: var(--p-red-300);
}

.add-button-container {
  display: flex;
  justify-content: flex-start;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background-color: var(--p-primary-color);
  color: var(--p-primary-0);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.add-btn:hover {
  background-color: var(--p-primary-600);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.add-btn i {
  font-size: 0.9rem;
}
</style>
